import { v4 as uuid } from 'uuid';

import type { ExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import { HiddenPart } from 'src/containers/ProjectDiagram/templates/extensions/hiddenPart';
import { ExtendedLink } from 'src/containers/ProjectDiagram/templates/extensions/link';
import { ExtendedNode } from 'src/containers/ProjectDiagram/templates/extensions/node';
import {
  DeletedMapping,
  DeletedMappingLabel,
  DeletedModelConceptClass,
  DeletedModelConceptHierarchy,
  DeletedModelConceptHierarchyLabel,
  DeletedModelConceptRelationship,
  DeletedModelConceptRelationshipLabel,
  DeletedRelationshipMapping,
  DeletedResource,
  FileUpload,
  Mapping,
  MappingLabel,
  ModelConceptClass,
  ModelConceptHierarchy,
  ModelConceptHierarchyLabel,
  ModelConceptRelationship,
  ModelConceptRelationshipLabel,
  RelationshipMapping,
  Resource,
  VirtualGraph,
} from 'src/containers/ProjectDiagram/types/data';
import {
  ModelSnapshot,
  createEmptyModelSnapshot,
} from 'src/containers/ProjectDiagram/types/model';

// resources

export interface ResourceNode extends ExtendedNode {
  key: string;
  data: Resource;
}

export interface FileUploadNode extends ResourceNode {
  key: string;
  data: FileUpload;
}

export interface VirtualGraphNode extends ResourceNode {
  key: string;
  data: VirtualGraph;
}

// deleted resources

export interface DeletedResourceNode extends ExtendedNode {
  key: string;
  data: DeletedResource;
}

// mappings

export interface MappingLabelNode extends ExtendedNode {
  key: string;
  data: MappingLabel;
  labeledLink: MappingLink | null;
}

export interface MappingLink extends ExtendedLink {
  key: string;
  data: Mapping;
  fromNode: ResourceNode | FileUploadNode | VirtualGraphNode;
  toNode: ModelConceptClassNode | ModelConceptRelationshipLabelNode | null;
  labelNodes: go.Iterator<MappingLabelNode>;
}
export interface RelationshipMappingLink extends ExtendedLink {
  key: string;
  data: RelationshipMapping;
  fromNode: MappingLabelNode | ModelConceptClassNode;
  toNode: MappingLabelNode | ModelConceptClassNode;
}

// deleted mappings

export interface DeletedMappingLabelNode extends ExtendedNode {
  key: string;
  data: DeletedMappingLabel;
  labeledLink: DeletedMappingLink | null;
}

export interface DeletedMappingLink extends ExtendedLink {
  key: string;
  data: DeletedMapping;
  fromNode:
    | ResourceNode
    | FileUploadNode
    | VirtualGraphNode
    | DeletedResourceNode;
  toNode:
    | ModelConceptClassNode
    | ModelConceptRelationshipLabelNode
    | DeletedModelConceptClassNode
    | DeletedModelConceptRelationshipLabelNode
    | null;
  labelNodes: go.Iterator<DeletedMappingLabelNode>;
}
export interface DeletedRelationshipMappingLink extends ExtendedLink {
  key: string;
  data: DeletedRelationshipMapping;
  fromNode:
    | MappingLabelNode
    | ModelConceptClassNode
    | DeletedMappingLabelNode
    | DeletedModelConceptClassNode;
  toNode:
    | MappingLabelNode
    | ModelConceptClassNode
    | DeletedMappingLabelNode
    | DeletedModelConceptClassNode;
}

// model concepts

export interface ModelConceptClassNode extends ExtendedNode {
  key: string;
  data: ModelConceptClass;
}

export interface ModelConceptRelationshipLabelNode extends ExtendedNode {
  key: string;
  data: ModelConceptRelationshipLabel;
  labeledLink: ModelConceptRelationshipLink | null;
}

export interface ModelConceptRelationshipLink extends ExtendedLink {
  key: string;
  data: ModelConceptRelationship;
  fromNode: ModelConceptClassNode | null;
  labelNodes: go.Iterator<ModelConceptRelationshipLabelNode>;
  toNode: ModelConceptClassNode | null;
}

export interface ModelConceptHierarchyLabelNode extends ExtendedNode {
  key: string;
  data: ModelConceptHierarchyLabel;
  labeledLink: ModelConceptHierarchyLink | null;
}

export interface ModelConceptHierarchyLink extends ExtendedLink {
  key: string;
  data: ModelConceptHierarchy;
  fromNode: ModelConceptClassNode;
  labelNodes: go.Iterator<ModelConceptHierarchyLabelNode>;
  toNode: ModelConceptClassNode;
}

// deleted model concepts

export interface DeletedModelConceptClassNode extends ExtendedNode {
  key: string;
  data: DeletedModelConceptClass;
}

export interface DeletedModelConceptRelationshipLabelNode extends ExtendedNode {
  key: string;
  data: DeletedModelConceptRelationshipLabel;
  labeledLink: DeletedModelConceptRelationshipLink | null;
}

export interface DeletedModelConceptRelationshipLink extends ExtendedLink {
  key: string;
  data: DeletedModelConceptRelationship;
  fromNode: ModelConceptClassNode | DeletedModelConceptClassNode | null;
  labelNodes: go.Iterator<DeletedModelConceptRelationshipLabelNode>;
  toNode: ModelConceptClassNode | DeletedModelConceptClassNode | null;
}

export interface DeletedModelConceptHierarchyLabelNode extends ExtendedNode {
  key: string;
  data: DeletedModelConceptHierarchyLabel;
  labeledLink: DeletedModelConceptHierarchyLink | null;
}

export interface DeletedModelConceptHierarchyLink extends ExtendedLink {
  key: string;
  data: DeletedModelConceptHierarchy;
  fromNode: ModelConceptClassNode | DeletedModelConceptClassNode;
  labelNodes: go.Iterator<DeletedModelConceptHierarchyLabelNode>;
  toNode: ModelConceptClassNode | DeletedModelConceptClassNode;
}

export type DiagramNode =
  | ResourceNode
  | FileUploadNode
  | VirtualGraphNode
  | ModelConceptClassNode
  | DeletedResourceNode
  | DeletedModelConceptClassNode;
export type DiagramLinkLabel =
  | MappingLabelNode
  | ModelConceptHierarchyLabelNode
  | ModelConceptRelationshipLabelNode
  | DeletedMappingLabelNode
  | DeletedModelConceptHierarchyLabelNode
  | DeletedModelConceptRelationshipLabelNode;
export type DiagramLink =
  | MappingLink
  | RelationshipMappingLink
  | ModelConceptHierarchyLink
  | ModelConceptRelationshipLink
  | DeletedMappingLink
  | DeletedRelationshipMappingLink
  | DeletedModelConceptHierarchyLink
  | DeletedModelConceptRelationshipLink;
export type DiagramPart = HiddenPart;
export type DiagramGraphObject =
  | DiagramNode
  | DiagramLinkLabel
  | DiagramLink
  | DiagramPart;

export type Diagram = ExtendedDiagram;

export interface DiagramSnapshot {
  id: string;
  model: ModelSnapshot;
}

export const createEmptyDiagramSnapshot = (): DiagramSnapshot => ({
  id: uuid(),
  model: createEmptyModelSnapshot(),
});
